import { thkDisplay, lenDisplay, qtyDisplay } from "@/utils/quoteDisplay";

const fmt = (n) => "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const fmtDate = (s) => {
  try {
    return new Date(s).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return s;
  }
};

// The customer-facing quotation document — used in the archive view and in New Quote preview.
export const QuotationSheet = ({ quote }) => (
  <div data-testid="quotation-sheet">
    <div className="border-b-2 border-royal pb-5 mb-6">
      {quote.company?.logo && <img src={quote.company.logo} alt="Company logo" className="h-14 mb-3" data-testid="letterhead-logo" />}
      <p className="font-heading font-semibold uppercase text-3xl text-royal">{quote.company?.name}</p>
      <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/60 mt-1">{quote.company?.tagline}</p>
      <p className="font-body text-xs text-ink/60 mt-2">
        {quote.company?.address} · {quote.company?.phones} · {quote.company?.email}
        {quote.company?.gstin ? ` · GSTIN: ${quote.company.gstin}` : ""}
      </p>
    </div>

    <div className="flex justify-between mb-6 font-body text-sm">
      <div>
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50">To</p>
        <p className="font-heading font-semibold uppercase text-xl">{quote.customer?.company}</p>
        {quote.customer?.site && <p className="text-ink/60 text-xs">{quote.customer.site}</p>}
        {quote.customer?.contact && <p className="text-ink/60 text-xs">{quote.customer.contact}{quote.customer.mobile ? ` · ${quote.customer.mobile}` : ""}</p>}
        {quote.customer?.gst_no && <p className="text-ink/60 text-xs">GST: {quote.customer.gst_no}</p>}
      </div>
      <div className="text-right">
        <p className="font-mono text-xs text-royal">{quote.quote_no}</p>
        <p className="text-ink/60 text-xs">{fmtDate(quote.created_at)}</p>
      </div>
    </div>

    <table className="w-full mb-6">
      <thead>
        <tr className="border-b border-ink/20 font-mono text-[10px] tracking-[0.2em] uppercase text-ink/60 text-left">
          <th className="py-2 pr-2">Description</th>
          <th className="py-2 pr-2">Size (mm)</th>
          <th className="py-2 pr-2">THK</th>
          <th className="py-2 pr-2">Len</th>
          <th className="py-2 pr-2">Qty</th>
          <th className="py-2 pr-2 text-right">Unit Price</th>
          <th className="py-2 text-right">Amount</th>
        </tr>
      </thead>
      <tbody>
        {quote.items?.map((it, i) => (
          <tr key={i} className="border-b border-ink/10 font-body text-sm">
            <td className="py-2.5 pr-2">{it.description}</td>
            <td className="py-2.5 pr-2 font-mono text-xs">{it.f1}×{it.width}×{it.f2}</td>
            <td className="py-2.5 pr-2 font-mono text-xs">{thkDisplay(it)}</td>
            <td className="py-2.5 pr-2 font-mono text-xs">{lenDisplay(it)}</td>
            <td className="py-2.5 pr-2 font-mono text-xs">{qtyDisplay(it)}</td>
            <td className="py-2.5 pr-2 font-mono text-xs text-right">
              {fmt(it.calc?.unit_price)}{it.price_format === "per_meter" ? "/m" : it.price_format === "per_kg" ? "/kg" : "/pc"}
            </td>
            <td className="py-2.5 font-mono text-xs text-right">{fmt(it.calc?.line_total)}</td>
          </tr>
        ))}
      </tbody>
    </table>

    <div className="flex justify-end mb-6">
      <div className="font-body text-sm w-64">
        <p className="flex justify-between py-1"><span className="text-ink/60">Subtotal</span><span className="font-mono">{fmt(quote.subtotal)}</span></p>
        <p className="flex justify-between py-1"><span className="text-ink/60">GST ({quote.gst_pct}%)</span><span className="font-mono">{fmt(quote.gst_amount)}</span></p>
        <p className="flex justify-between py-2 border-t border-ink/20 font-heading font-semibold text-lg"><span>Grand Total</span><span className="font-mono text-royal">{fmt(quote.grand_total)}</span></p>
      </div>
    </div>

    {quote.notes && (
      <p className="font-body text-sm text-ink/70 mb-4">
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50 block mb-1">Notes</span>
        {quote.notes}
      </p>
    )}

    {quote.terms?.length > 0 && (
      <div className="border-t border-ink/20 pt-4 mb-6">
        <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink/50 mb-2">Terms & Conditions</p>
        <ol className="font-body text-xs text-ink/70 list-decimal list-inside flex flex-col gap-1">
          {quote.terms.map((t, i) => <li key={i}>{t}</li>)}
        </ol>
      </div>
    )}

    <div className="flex justify-between items-end mt-8" data-testid="quote-signature-block">
      <p className="font-body text-xs text-ink/60">
        Prepared by{" "}<span className="font-semibold text-ink">{quote.created_by || quote.company?.name}</span>{" "}on {fmtDate(quote.created_at)}
      </p>
      <div className="text-right">
        <p className="font-body text-sm text-ink mb-10">For {quote.company?.name}</p>
        <p className="border-t border-ink/40 pt-1 font-body text-xs text-ink/60">Authorised Signatory</p>
      </div>
    </div>
  </div>
);
